import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { useCallback, useEffect, useState } from "react";
import Button, { ButtonType } from "../../components/Button";
import DataTable from "../../components/DataTable";
import LoadingSpinner from "../../components/loadingSpinner/LoadingSpinner";
import useFetch from '../../hooks/useFetch';
import useSignalR from '../../hooks/useSignalR';
import { formatDate } from "../../utils/formatting";
import AuthLogoutMessage from "../Auth/components/AuthLogoutMessage";
import { LogoutReason, useSession } from "../Auth/hooks/useSession";
import useLocalStorage from "./../../hooks/useLocalStorage";
import { ResellerType, getDisableUserModificationFlag, getUserResellerType } from "./../../utils/getUserInformation";
import AddCustomer from "./AddCustomer";
import ViewCustomer from "./ViewCustomer";

const ListCustomers = () => {
	const { reason } = useSession();
	const { connection, onConnected, registerHandler } = useSignalR();
	const [showAddModal, setAddModal] = useState(false);
	const [showViewModal, setViewModal] = useState(false);
	const [editing, setEditing] = useState(false);
	const [viewItemData, setViewItemData] = useState({});
	const { ready, executingFetch, execute } = useFetch();
	const [data, setData] = useState(null);
	const [resellerType, setResellerType] = useState(ResellerType.Invalid);
	const [disableUserModification, setDisableUserModification] = useState(false);

	useLocalStorage("ap-liu", () => {
		setResellerType(getUserResellerType());
		setDisableUserModification(getDisableUserModificationFlag());
	});

	const getDataFromServer = useCallback(() => {
		execute("/api/Customers/", null, (err, content) => {
			if (err) {
				console.error(err);
			}
			else {
				setData(content);
			}
		});
	}, [execute]);

	// Hook to make initial fetch request.
	useEffect(() => {
		getDataFromServer();
	}, [getDataFromServer]);

	// Hook that joins the shared "Customers" group once.
	useEffect(() => {
		if (!connection) {
			return;
		}

		onConnected(async (conn) => {
			try {
				await conn.invoke("JoinGroupAsync", "Customers");
				console.debug("Joined group: Customers");
			}
			catch (e) {
				console.error("JoinGroupAsync failed", e);
			}
		});

		connection.onreconnected(async () => {
			console.debug("Reconnected — joining group again...");
			await connection.invoke("JoinGroupAsync", "Customers");
		});
	}, [connection]);

	const handleCustomer = (msg) => {
		if (msg.success) {
			getDataFromServer();
		}
		else {
			console.error(msg.error);
		}
	};

	// Register SignalR message handlers.
	useEffect(() => {
		if (!connection || !ready) {
			return;
		}

		const unregisterAdd = registerHandler("ProvisionAdd", "Customers", handleCustomer);
		const unregisterDelete = registerHandler("ProvisionDelete", "Customers", handleCustomer);

		return () => {
			unregisterAdd();
			unregisterDelete();
		};
	}, [connection, ready, registerHandler]);

	function onClickCustomer(adding, item) {
		if (adding) {
			setAddModal(true);
		}
		else if (item.data.CompanyId) {
			setEditing(false);
			setViewItemData(item.data);
			setViewModal(true);
		}
	}

	const columns = [
		{
			field: "CompanyName",
			headerName: "Name",
			valueGetter: (data) => { return (data.CompanyId) ? data.CompanyName : <em className="grey">Provision pending:&nbsp;{data.CompanyName}</em>; }
		},
		{
			field: "Packages",
			headerName: "Packages",
			valueGetter: (data) => {
				if (!Array.isArray(data.Packages) || data.Packages.length === 0) {
					return <span className="grey">No packages</span>;
				}
				return data.Packages.map((p, index) => (
					<div key={index}>{p.PackageName}</div>
				));
			}
		},
		{
			field: "Seats",
			headerName: "Seats",
			valueGetter: (data) => {
				if (!Array.isArray(data.Packages) || data.Packages.length === 0) {
					return <span className="grey">-</span>;
				}
				return data.Packages.map((p, index) => (
					<div key={index}>{p.Seats}</div>
				));
			}
		},
		{
			field: "Provider",
			headerName: "Provider"
		},
		{
			field: "CreationDate",
			headerName: "Date Created",
			valueGetter: (data) => { return formatDate(data.CreationDate); }
		}
	];

	return (
		<div>
			{(reason !== LogoutReason.None)
				? <AuthLogoutMessage reason={reason} />
				: (<>
					<h1>{resellerType === ResellerType.NonBillable ? "My ucSKILLS Customers" : "ucSKILLS Access" }</h1>					
					{resellerType === ResellerType.Billable && 
						 <p className="mb-6">Give access to ucSKILLS to your customers or sub-resellers. Here you can provision users on ucSKILLS and set their offer.</p>
					}
					{resellerType === ResellerType.NonBillable &&
						<p className="mb-6">Here you will find your customers who have been provisioned on ucSKILLS.</p>
					}
					{executingFetch
						? <LoadingSpinner active={executingFetch} />
						: (<DataTable columns={columns} rows={data} columnIdName="Id" onRowClick={(params) => onClickCustomer(false, params)} />)
					}
					{!disableUserModification && (
						<Button type={ButtonType.Secondary} icon={faCirclePlus} label={"Add New Customer"} onClick={() => onClickCustomer(true)} />
					)}
					{showAddModal &&
						<AddCustomer showModal={showAddModal} setModal={setAddModal} getDataFromServer={getDataFromServer} />
					}
					{showViewModal &&
						<ViewCustomer open={!editing}
							showModal={showViewModal}
							setModal={setViewModal}
							item={viewItemData}
							disableUserModification={disableUserModification}
							onPackagesUpdate={(newPackages) => {
								setViewItemData((prev) => ({
									...prev,
									Packages: newPackages
								}));
							}}
							getDataFromServer={getDataFromServer}
						/>
					}
				</>)
			}
		</div>
	);
};

export default ListCustomers;